import cx from 'classnames';
import { AlertIcon } from '../icons';
import './FieldGrid.css';

/**
 * QHS FieldGrid + GField
 * ----------------------
 * A 12-column grid for form fields — intended for dense account /
 * settings pages where inputs sit next to labels with varying
 * widths. Prefer `FieldGroup.Grid` when you just need a 2-3 column
 * card of inputs with the same width.
 *
 *   <FieldGrid>
 *     <GField span={6} label="Business name" required>
 *       <Input fullWidth name="businessName" />
 *     </GField>
 *     <GField span={6} label="Trade name">
 *       <Input fullWidth name="tradeName" />
 *     </GField>
 *     <GField span={4} label="Phone" required error={errors.phone}>
 *       <Input fullWidth name="phone" />
 *     </GField>
 *   </FieldGrid>
 *
 * `GField` renders its own small uppercase label (so multiple
 * `GField`s in a row share a consistent tone); the Input / Select
 * inside stays label-less. `hint` and `error` mirror the messaging
 * slot already present on Input/Select — pick one spot per field,
 * not both.
 */
const FieldGrid = ({ gap = 16, columns = 12, className, children, style, ...rest }) => (
  <div
    className={cx('qhs-field-grid', className)}
    style={{ '--qhs-fg-gap': `${gap}px`, '--qhs-fg-cols': columns, ...style }}
    {...rest}
  >
    {children}
  </div>
);

const clampSpan = (value, max = 12) => {
  if (value == null) return max;
  const n = Number(value);
  if (!Number.isFinite(n)) return max;
  return Math.max(1, Math.min(max, Math.round(n)));
};

const GField = ({
  span,
  label,
  required,
  hint,
  error,
  htmlFor,
  className,
  children,
  style,
  ...rest
}) => {
  const colSpan = clampSpan(span);
  // The span the field takes once the grid is too narrow for twelve
  // columns (see the container query in FieldGrid.css): anything up to
  // half the row keeps half, anything wider takes the whole row.
  const narrowSpan = colSpan <= 6 ? 6 : 12;
  return (
    <div
      className={cx('qhs-gfield', error && 'qhs-gfield--error', className)}
      style={{ '--qhs-gfield-span': colSpan, '--qhs-gfield-span-narrow': narrowSpan, ...style }}
      {...rest}
    >
      {label && (
        <label htmlFor={htmlFor} className='qhs-gfield__label'>
          {label}
          {required && (
            <span className='qhs-gfield__req' aria-hidden='true'>
              *
            </span>
          )}
        </label>
      )}
      <div className='qhs-gfield__control'>{children}</div>
      {(error || hint) && (
        <span
          className={cx(
            'qhs-gfield__msg',
            error ? 'qhs-gfield__msg--error' : 'qhs-gfield__msg--hint'
          )}
        >
          {error && <AlertIcon className='qhs-gfield__msg-icon' fontSize='inherit' />}
          {error || hint}
        </span>
      )}
    </div>
  );
};

FieldGrid.Item = GField;

export { FieldGrid, GField };
export default FieldGrid;
